import { ModuleDataSyncService } from '@application/persistence/ModuleDataSyncService';
import { uuid } from '@shared/utils/uuid';
import type { Conge } from '@shared/types/Conge';
import type { Salarie } from '@shared/types/Salarie';
import { CongesService } from '@domain/rh/CongesService';
import { loadStcConfig } from '@application/rh/StcConfig';

const NAMESPACE = 'rh.conges';
const LEGACY_KEY = 'nexxus.rh.conges';

type CongesPayload = { items: Conge[] };

export async function listConges(tenantId: string): Promise<Conge[]> {
  const data = await ModuleDataSyncService.getJson<CongesPayload>(
    tenantId,
    NAMESPACE,
    { items: [] },
    LEGACY_KEY,
  );
  return Array.isArray(data.items) ? data.items : [];
}

export async function saveConges(tenantId: string, items: Conge[]): Promise<void> {
  await ModuleDataSyncService.setJson(tenantId, NAMESPACE, { items }, LEGACY_KEY);
}

export async function upsertConge(tenantId: string, conge: Conge): Promise<Conge> {
  const items = await listConges(tenantId);
  const index = items.findIndex((c) => c.id === conge.id);
  if (index >= 0) items[index] = conge;
  else items.push(conge);
  await saveConges(tenantId, items);
  return conge;
}

export async function creerDemandeConge(
  tenantId: string,
  salarie: Salarie,
  input: {
    type: Conge['type'];
    date_debut: string;
    date_fin: string;
    motif?: string;
    statut?: Conge['statut'];
  },
): Promise<Conge> {
  const [existants, stc] = await Promise.all([listConges(tenantId), loadStcConfig(tenantId)]);
  const demande = CongesService.creerDemande(
    salarie,
    existants,
    {
      ...input,
      tenant_id: tenantId,
      id: uuid('conge'),
    },
    stc.conges,
  );
  return upsertConge(tenantId, demande);
}

export async function transiterConge(
  tenantId: string,
  congeId: string,
  next: Conge['statut'],
  actorId?: string,
): Promise<Conge> {
  const items = await listConges(tenantId);
  const current = items.find((c) => c.id === congeId);
  if (!current) throw new Error('Demande introuvable');
  const updated = CongesService.transiter(current, next, actorId);
  return upsertConge(tenantId, updated);
}
